// Vuex
import { mapState, mapActions, mapGetters, mapMutations } from 'vuex';
import uniqBy from 'lodash/uniqBy';
import EventBus from '@/elements/eventBus.js';
import { isProduction } from '@/elements/utils.js';
import { getMemorizedSearch } from '@/utils/fileExplorerSearch.js';
import gettext from '@/utils/translationInjector.js';
const { $gettext, interpolate } = gettext;

const FILES_COUNT_PER_LOOP = 50;

export default {
  data() {
    return {
      breadcrumbs: {},
      fileListQueue: null,
      isFinished: false,
      lastItem: null,
      parentItem: null,
      currentDirModel: null,
    };
  },
  computed: {
    ...mapState({
      collection: (state) => state.files.fileList,
      homeId: (state) => state.profile.homeId,
      rootModel: (state) => state.files.rootModel,
      profileId: (state) => state.profile.id,
      trashModel: (state) => state.files.trashModel,
    }),
    ...mapGetters({
      getTmpModel: 'files/getTmpModel',
      parseFile: 'files/parseModel',
      filesIds: 'files/filesIds',
    }),
    parsedCollection() {
      return this.resetCache();
    },
    sortParams() {
      return (this.sortKey && this.$store.state.sorting[this.sortKey]) || {};
    },
  },
  methods: {
    ...mapMutations({
      addFile: 'files/addFile',
      addArrayOfFiles: 'files/addArrayOfFiles',
      clearFiles: 'files/clearFiles',
      setFiles: 'files/setFiles',
      setTrashModel: 'files/setTrashModel',
    }),
    ...mapActions({
      getFiles: 'files/getFiles',
    }),
    getUniqueFiles(files) {
      const uniqueFiles = [];
      const unique = uniqBy(files, 'id');
      for (const file of unique) {
        uniqueFiles.push(this.parseFile(file, this.parseModel));
      }
      return uniqueFiles;
    },
    handleFileList: function* (files) {
      const uniqueFiles = this.getUniqueFiles(files).sort(this.comparator);
      let pointer = 0;
      //Initial bunch of files
      const firstStepCount = Math.min(FILES_COUNT_PER_LOOP, uniqueFiles.length);
      this.addArrayOfFiles(uniqueFiles.slice(pointer, firstStepCount));
      pointer += firstStepCount;
      if (pointer === uniqueFiles.length) {
        return;
      }
      yield;

      //Add next bunches of files with small timeout
      while (pointer + FILES_COUNT_PER_LOOP < uniqueFiles.length) {
        const nextStep = pointer + FILES_COUNT_PER_LOOP;
        this.addArrayOfFiles(uniqueFiles.slice(pointer, nextStep));
        pointer += FILES_COUNT_PER_LOOP;
      }

      this.addArrayOfFiles(uniqueFiles.slice(pointer));
      return;
    },
    comparator(model1, model2) {
      // go to previousDir on the top

      if (model1.isPreviousDir) {
        return -1;
      }
      if (model2.isPreviousDir) {
        return 1;
      }

      // Group system folders
      const isSystem1 = model1.metadata && model1.metadata.is_system;
      const isSystem2 = model2.metadata && model2.metadata.is_system;
      if (isSystem1 && !isSystem2) {
        return -1;
      }
      if (isSystem2 && !isSystem1) {
        return 1;
      }
      if (isSystem1 && isSystem2) {
        return model1.metadata.visual_order < model2.metadata.visual_order ? -1 : 1;
      }

      // Group special folders
      if (model1.isProjectFolder && !model2.isProjectFolder) {
        return -1;
      }
      if (model2.isProjectFolder && !model1.isProjectFolder) {
        return 1;
      }

      // Group folders
      if (model1.type === 'D' && ['F', 'N'].includes(model2.type)) {
        return -1;
      }
      if (['F', 'N'].includes(model1.type) && model2.type === 'D') {
        return 1;
      }

      // Sort by sort field
      const asc = this.sortParams.lastDir === -1 ? -1 : 1;
      let sortValue1 = model1[this.sortParams.sortAttr.id];
      let sortValue2 = model2[this.sortParams.sortAttr.id];

      if (typeof sortValue1 === 'string' && typeof sortValue2 === 'string') {
        const tmpValue1 = sortValue1;
        const tmpValue2 = sortValue2;
        sortValue1 = sortValue1.toLowerCase();
        sortValue2 = sortValue2.toLowerCase();

        if (sortValue1 === sortValue2) {
          sortValue1 = tmpValue1;
          sortValue2 = tmpValue2;
        }
      }

      return sortValue1 > sortValue2 ? asc : asc * -1;
    },
    setLastItem(lastItem) {
      this.lastItem = lastItem;
    },
    openDir(dir = this.currentDir, options = {}) {
      this.isDataLoading = !this.spinnerId;
      this.isFinished = false;
      this.checkedRowsIds = [];
      const memorizedSearch = getMemorizedSearch(this.profileId);
      const actuallyHasSearch = dir === 'search_results' && memorizedSearch;
      const params = {
        dir,
        success: () => {
          if (this.currentDirModel) {
            EventBus.$emit('breadcrumbs:update', this.currentDirModel);
          }
          if (options.checked) {
            const checkedFile = this.getFileById(options.checked);
            if (checkedFile) {
              checkedFile.isCheckedState = true;
            }
          }
          this.successOpenDir?.(dir, this.currentDirModel);
        },
        error: (response) => {
          !isProduction && console.error(response);
          const curDirName = this.currentDirModel?.name;
          this.spinnerId = '';
          this.isDataLoading = false;
          if (response?.status === 404) {
            if (this.lastItem) {
              EventBus.$emit('breadcrumbs:update', this.lastItem);
              this.openDir(this.lastItem.id);
            } else {
              this.openDir(this.homeDir);
            }
            if (curDirName) {
              EventBus.$emit('handle-error', {
                response,
                options: {
                  errorMsg: interpolate($gettext('Folder %{ name } not found'), {
                    name: curDirName,
                  }),
                },
              });
            }
          } else {
            if (curDirName) {
              EventBus.$emit('handle-error', {
                response,
                options: {
                  errorMsg: interpolate($gettext('Folder %{ name } not found. Refresh file list and try again.'), {
                    name: curDirName,
                  }),
                },
              });
            } else {
              EventBus.$emit('handle-error', { response });
            }
          }
        },
      };
      if (dir === 'search_results') {
        if (actuallyHasSearch) {
          EventBus.$emit('searchActive', true);
        } else {
          this.openDir(this.homeId);
        }
      } else if (this.includeHome && this.parentItem && dir === this.parentItem.id) {
        EventBus.$emit('searchActive', false);
        this.clearFiles();
        this.setFiles([this.rootModel]);
        this.successGetCollection(params);
      } else {
        this.getFileList(params);
      }
    },
    async getFileList(options = {}) {
      try {
        const { file, files } = await this.getFiles(options.dir);
        this.currentDirModel = this.parseFile(file);

        if (file.type === 'F') {
          this.openDir(file.parent_id, { checked: file.id });
          return;
        }

        if (file.type === 'T' && !this.trashModel?.metadata?.is_system) {
          this.setTrashModel(file);
        }

        if (this.includeHome && options.dir === this.homeId && !this.parentItem) {
          this.parentItem = { ...this.getTmpModel, id: file.parent_id };
        }

        this.fileListQueue = this.handleFileList(files);
        this.isFinished = this.fileListQueue.next().done;

        this.successGetCollection(options);
      } catch (error) {
        !isProduction && console.error(error);
        typeof options.error === 'function' && options.error(error.response);
      }
    },
    successGetCollection(options = {}) {
      typeof options.success === 'function' && options.success();

      this.spinnerId = '';
      this.isDataLoading = false;

      if (!this.isFinished) {
        this.fileListQueue.next();
        this.isFinished = true;
      }
    },
    resetCache() {
      const dir = this.currentDir || this.homeId;
      const cache = [];
      const noPrevDir = [this.homeId, 'search_results'];

      if (this.includeHome && this.parentItem) {
        noPrevDir.push(this.parentItem.id);
      }
      // Add '..' row if not in root and not in search results
      if ((this.includeHome && dir === this.homeId) || !noPrevDir.includes(dir)) {
        if (this.currentDirModel) {
          let tmpFile = this.getTmpModel;
          tmpFile = {
            ...tmpFile,
            id: this.currentDirModel.parent_id,
            name: '..',
            isPreviousDir: true,
            size: '',
            isListed: false,
            modifiedFormatted: '',
            isActive: true,
            isActiveState: false,
            isCheckedState: false,
            isDisabledState: true,
            isCheckboxVisible: false,
          };
          cache.push(tmpFile);
        }
      }
      return cache.concat(this.collection.filter((file) => file.isListed === true));
    },
  },
};
